import { normalizeContact } from './defaultBootstrapState';
import { toYoutubeEmbedUrl } from '../Utils/youtubeUtils';

const idOf = (item) => item?.legacyId ?? item?.id;

const refId = (ref) => {
  if (!ref) return null;
  if (typeof ref === 'object') return ref.legacyId ?? ref.id;
  return ref;
};

const mapLookup = (items = []) =>
  items.map((item) => ({
    id: idOf(item),
    _id: item._id,
    name: item.name,
    image: item.image || '',
    ...(item.brand_id != null || item.brand
      ? { brand_id: item.legacyBrandId ?? item.brand_id ?? refId(item.brand) }
      : {}),
    ...(item.state_id != null || item.state
      ? { state_id: item.legacyStateId ?? item.state_id ?? refId(item.state) }
      : {}),
    ...(item.city_id != null || item.city
      ? { city_id: item.legacyCityId ?? item.city_id ?? refId(item.city) }
      : {}),
  }));

export const mapCars = (cars = []) =>
  cars.map((c) => ({
    id: idOf(c),
    _id: c._id,
    user_id: refId(c.user) ?? c.user_id,
    status: c.status,
    brand_id: refId(c.brand) ?? c.brand_id,
    model_id: refId(c.model) ?? c.model_id,
    body_type_id: refId(c.body_type) ?? c.body_type_id,
    fuel_type_id: refId(c.fuel_type) ?? c.fuel_type_id,
    transmission_id: refId(c.transmission) ?? c.transmission_id,
    seats_id: refId(c.seats) ?? c.seats_id,
    color_id: refId(c.color) ?? c.color_id,
    feature_ids: (c.features || c.feature_ids || []).map((f) =>
      typeof f === 'object' ? idOf(f) : f
    ),
    year: c.year,
    tire_wear: c.tire_wear,
    km_driven: c.km_driven,
    price: c.price,
    images: c.images || [],
    youtube: toYoutubeEmbedUrl(c.youtube) || c.youtube || '',
    shorts: toYoutubeEmbedUrl(c.shorts) || c.shorts || '',
    location: c.location || '',
    view_count: c.view_count || 0,
    state_id: refId(c.state) ?? c.state_id,
    city_id: refId(c.city) ?? c.city_id,
    sub_register_id: refId(c.sub_register) ?? c.sub_register_id,
    is_modify: c.is_modify ?? 0,
    is_project: c.is_project ?? 0,
    home_show: c.home_show !== false,
    inspection: c.inspection || {},
    warranty: c.warranty || {},
    testDriveEnabled: c.testDriveEnabled !== false,
    createdAt: c.createdAt || null,
  }));

export const mapPartners = (partners = []) =>
  partners.map((p) => ({
    id: idOf(p),
    _id: p._id,
    name: p.name,
    email: p.email || '',
    ph_num: p.ph_num,
    profile_image: p.profile_image || '',
    isActive: p.isActive !== false,
    state_id: refId(p.state),
    city_id: refId(p.city),
    sub_register_id: (p.sub_registers || p.sub_register_id || []).map((s) =>
      typeof s === 'object' ? idOf(s) : s
    ),
  }));

export const mapAdminContact = (adminContact) => {
  if (!adminContact) return null;
  return {
    id: adminContact.id || 'admin',
    _id: adminContact._id || 'admin',
    name: adminContact.name || 'CarsInfinite Admin',
    ph_num: adminContact.ph_num,
    profile_image: adminContact.profile_image || '',
    isAdminFallback: true,
  };
};

export const mapHero = (hero) => {
  if (!hero) return { carousel: [], heroType: [] };
  return {
    carousel: (hero.carousel || []).map((item) => ({
      id: idOf(item),
      image: item.image || '',
      text: item.text || '',
      kicker: item.kicker || '',
      subtext: item.subtext || '',
    })),
    heroType: (hero.heroType || []).map((item) => ({
      id: idOf(item),
      name: item.name,
    })),
  };
};

export const mapAppPromo = (promo) => {
  if (!promo) return {};
  const data = promo.data || promo;
  return {
    title: data.title || '',
    subtitle: data.subtitle || '',
    description: data.description || '',
    phoneImage: data.phoneImage || '',
    storeButtons: (data.storeButtons || []).map((button) => ({
      id: idOf(button),
      name: button.name || '',
      image: button.image || '',
      link: button.link || '#',
    })),
  };
};

export const mapBootstrapData = (apiData) => {
  const hero = mapHero(apiData.hero);
  const site = apiData.siteConfig || {};

  return {
    cars: mapCars(apiData.cars),
    brands: mapLookup(apiData.brands),
    model: mapLookup(apiData.models),
    body_type: mapLookup(apiData.bodyTypes),
    fuel_type: mapLookup(apiData.fuelTypes),
    transmission: mapLookup(apiData.transmissions),
    seats: mapLookup(apiData.seats),
    car_color: mapLookup(apiData.carColors),
    featuers: mapLookup(apiData.features),
    partners: mapPartners(apiData.partners),
    sub_register: mapLookup(apiData.subRegisters),
    state: mapLookup(apiData.states),
    city: mapLookup(apiData.cities),
    carousel: hero.carousel,
    heroType: hero.heroType,
    ads: apiData.ad
      ? {
          id: idOf(apiData.ad),
          car_id: refId(apiData.ad.car) ?? apiData.ad.legacyCarId,
          image: apiData.ad.image || '',
          duration: apiData.ad.duration || 5000,
        }
      : null,
    home_page: apiData.homePage
      ? {
          cardLimit: apiData.homePage.cardLimit ?? 8,
          sections: (apiData.homePage.sections || []).map((s) => ({
            id: idOf(s),
            key: s.key,
            show: s.show !== false,
            title: s.title,
            highlight: s.highlight,
            type: s.sectionType || s.type,
            filter: s.filter || {},
          })),
        }
      : {},
    contact: normalizeContact(apiData.contact),
    privacy: apiData.privacy || {},
    aboutUs: apiData.aboutUs || {},
    whychooseus: apiData.whyChooseUs || [],
    appPromo: mapAppPromo(apiData.appPromo),
    auth: site.auth || {},
    header: site.header || {},
    profile: site.profile || { tabs: [], sections: {}, kycForm: {}, profileFields: [], passwordSettings: {} },
    sellcar_form: site.sellcar?.form || site.sellcar_form || {},
    sellcar_page: site.sellcar?.page || site.sellcar_page || {},
    valuation_page: (() => {
      const val = site.valuation || {};
      if (val.page) return val.page;
      if (val.form) return {};
      return val;
    })(),
    valuation_form: site.valuation?.form || [],
    compare_page: site.compare?.page || site.compare || {},
    car_trust: site.carTrust?.page || site.carTrust || {},
    emi_page: site.emi?.page || site.emi || {},
    warranty_page: site.warranty || {},
    modified_cars: mapCars((apiData.cars || []).filter((c) => c.is_modify === 1)),
    car_projects: mapCars((apiData.cars || []).filter((c) => c.is_project === 1)),
    adminContact: mapAdminContact(apiData.adminContact),
  };
};
